import { create } from 'zustand';
import type { Airport } from '../../shared/components/common/SearchForm/searchFormTypes';
import { UserRole } from '../../types/user';
import { SEARCH_DEFAULTS } from '../../shared/styles/theme/theme';

interface SearchPayload {
  fromLocation: number | string;
  toLocation?: number | string | null;
  startDate: string;
  endDate: string;
  latitude: number;
  longitude: number;
  radius: number;
  page: number;
  limit: number;
  sortBy?: string;
  weight?: number;
  weightUnit?: "kg" | "lb";
}

interface SearchState {
  // Form data
  from: Airport | null;
  to: Airport | null;
  startDate: Date | null;
  endDate: Date | null;

  // Search state
  searchPayload: SearchPayload | null;
  userRole: UserRole | null;
  searchForRole: UserRole.SENDER | UserRole.TRAVELLER;

  // UI state
  fromSearch: string;
  toSearch: string;
  userCity: string;
  errors: {
    from: boolean;
    to: boolean;
    dateRange: boolean;
  };
  fromDropdownOpen: boolean;
  toDropdownOpen: boolean;
  formSubmitted: boolean;
  datePickerOpen: boolean;
  filterDialogOpen: boolean;

  // Filter state
  filters: {
    radius?: number;
    minWeight?: number;
    maxWeight?: number;
    weightUnit: "lb";
    sortBy?: "latest" | "price_high_to_low" | "price_low_to_high" | "rating_desc" | "rating_asc";
  };

  // actions
  setDatePickerOpen: (open: boolean) => void;
  setFrom: (airport: Airport | null) => void;
  setTo: (airport: Airport | null) => void;
  setDates: (startDate: Date | null, endDate: Date | null) => void;
  setFromSearch: (search: string) => void;
  setToSearch: (search: string) => void;
  setUserCity: (city: string) => void;
  setFromDropdownOpen: (open: boolean) => void;
  setToDropdownOpen: (open: boolean) => void;
  setError: (field: 'from' | 'to' | 'dateRange', hasError: boolean) => void;
  setFormSubmitted: (submitted: boolean) => void;
  setSearchPayload: (payload: SearchPayload | null) => void;
  setUserRole: (role: UserRole | null) => void;
  setSearchForRole: (role: UserRole.SENDER | UserRole.TRAVELLER) => void;
  setFilterDialogOpen: (open: boolean) => void;
  setFilters: (filters: Partial<SearchState['filters']>, replace?: boolean) => void;
  reset: () => void;
}

export const useSearchStore = create<SearchState>((set) => ({
  // Initial state
  from: null,
  to: null,
  startDate: null,
  endDate: null,
  searchPayload: null,
  userRole: null,
  searchForRole: UserRole.SENDER,
  fromSearch: '',
  toSearch: '',
  userCity: '',
  errors: {
    from: false,
    to: false,
    dateRange: false,
  },
  fromDropdownOpen: false,
  toDropdownOpen: false,
  formSubmitted: false,
  datePickerOpen: false,
  filterDialogOpen: false,

  filters: {
    radius: SEARCH_DEFAULTS.RADIUS,
    minWeight: SEARCH_DEFAULTS.MIN_WEIGHT,
    maxWeight: SEARCH_DEFAULTS.MAX_WEIGHT,
    weightUnit: SEARCH_DEFAULTS.WEIGHT_UNIT,
    sortBy: SEARCH_DEFAULTS.SORT_BY,
  },

  // actions
  setDatePickerOpen: (open) => set({ datePickerOpen: open }),
  setFrom: (airport) => set((state) => ({
    from: airport,
    errors: { ...state.errors, from: false }
  })),

  setTo: (airport) => set((state) => ({
    to: airport,
    errors: { ...state.errors, to: false }
  })),

  setDates: (startDate, endDate) => set((state) => ({
    startDate,
    endDate,
    errors: { ...state.errors, dateRange: false }
  })),

  setFromSearch: (search) => set((state) => ({
    fromSearch: search,
    fromDropdownOpen: search.length >= 2,
    ...(search === '' ? {
      from: null,
      errors: { ...state.errors, from: true }
    } : {})
  })),

  setToSearch: (search) => set((state) => ({
    toSearch: search,
    toDropdownOpen: search.length >= 2,
    ...(search === '' ? {
      to: null,
      errors: { ...state.errors, to: true }
    } : {})
  })),

  setUserCity: (city) => set({ userCity: city }),
  setFromDropdownOpen: (open) => set({ fromDropdownOpen: open }),
  setToDropdownOpen: (open) => set({ toDropdownOpen: open }),
  setError: (field, hasError) => set((state) => ({
    errors: { ...state.errors, [field]: hasError }
  })),

  setFormSubmitted: (submitted) => set({ formSubmitted: submitted }),
  setSearchPayload: (payload) => set({ searchPayload: payload }),
  setUserRole: (role) => set({ userRole: role }),
  setSearchForRole: (role) => set({ searchForRole: role }),
  setFilterDialogOpen: (open) => set({ filterDialogOpen: open }),
  setFilters: (filters, replace = false) => set((state) => ({
    filters: replace ? { weightUnit: state.filters.weightUnit, ...filters } : { ...state.filters, ...filters }
  })),
  reset: () => set({
    from: null,
    to: null,
    startDate: null,
    endDate: null,
    searchPayload: null,
    userRole: null,
    fromSearch: '',
    toSearch: '',
    errors: {
      from: false,
      to: false,
      dateRange: false,
    },
    formSubmitted: false,
    filterDialogOpen: false,
    filters: {
      radius: SEARCH_DEFAULTS.RADIUS,
      minWeight: SEARCH_DEFAULTS.MIN_WEIGHT,
      maxWeight: SEARCH_DEFAULTS.MAX_WEIGHT,
      weightUnit: SEARCH_DEFAULTS.WEIGHT_UNIT,
      sortBy: SEARCH_DEFAULTS.SORT_BY,
    },
  }),
}));
